/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { FormDefinition } from '../models/FormDefinition';
import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';
export class FormDefinitionService {
    constructor(public readonly httpRequest: BaseHttpRequest) {}
    /**
     * Retrieves all form definitions from the database, with optional filtering by name.
     * This endpoint returns a list of all form definitions, excluding those of type ComplianceItemDefinition.
     * The results can be filtered by providing a search query that matches against form names.
     * The returned form definitions do not include their components or rules to minimize payload size.
     * @param query Optional search query to filter form names by.
     * If provided, only forms with names containing this string (case-insensitive) will be returned.
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public getAllFormDefinitions(
        query?: string,
    ): CancelablePromise<Array<FormDefinition>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/FormDefinition/GetAllFormDefinitions',
            query: {
                'query': query,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Retrieves a single form definition by its unique identifier (GUID).
     * This endpoint returns a complete form definition including all its components (ordered by FormOrder),
     * component events, element rules, section rules, and form rules. It also includes the count of
     * form responses that have been submitted for this form definition.
     *
     * The query uses AsSplitQuery() to optimize the loading of multiple related collections.
     * @param formDefinitionGuid The unique identifier (GUID) of the form definition to retrieve
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public getFormDefinition(
        formDefinitionGuid: string,
    ): CancelablePromise<FormDefinition> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/FormDefinition/{formDefinitionGuid}/GetFormDefinition',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Updates an existing form definition with new content, including its components, rules, and events.
     * This is a complex operation that handles:
     * - Updating the form definition properties
     * - Managing form elements (components) including their order
     * - Handling element events
     * - Processing form rules, section rules, and element rules
     * - Deleting elements that are no longer present in the updated form
     *
     * The method uses a database transaction to ensure all changes are applied atomically.
     * If any form responses exist for this form, elements will be archived rather than deleted.
     *
     * After successful update, a real-time notification is sent to all clients viewing the form
     * via SignalR to inform them of the changes.
     * @param formDefinitionGuid The unique identifier (GUID) of the form definition to update
     * @param requestBody The complete updated form definition with all its components and rules
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public updateFormDefinition(
        formDefinitionGuid: string,
        requestBody?: FormDefinition,
    ): CancelablePromise<FormDefinition> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/api/FormDefinition/{formDefinitionGuid}/UpdateFormDefinition',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Updates the sort order of form definitions within a workflow.
     * This endpoint allows reordering the forms that make up a workflow.
     * The order of GUIDs in the provided list determines the new sort order of the forms.
     * Forms are assigned sequential sort order values (0, 1, 2, etc.) based on their position in the list.
     *
     * This is typically used when a user drags and drops forms to reorder them in the workflow designer UI.
     * @param workflowDefinitionGuid
     * @param requestBody An ordered list of form definition GUIDs representing the new desired order
     * @returns boolean OK
     * @throws ApiError
     */
    public updateWorkflowStepOrder(
        workflowDefinitionGuid: string,
        requestBody?: Array<string>,
    ): CancelablePromise<boolean> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/api/FormDefinition/{workflowDefinitionGuid}/UpdateWorkflowStepOrder',
            path: {
                'workflowDefinitionGuid': workflowDefinitionGuid,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Replaces an existing form definition's content with a new form structure provided as JSON.
     * This endpoint completely replaces an existing form's structure with new content.
     * It performs the following operations:
     * 1. Removes all existing components, rules, and events from the form
     * 2. Creates new sections from the uploaded form
     * 3. Creates new components and associates them with the appropriate sections
     * 4. Organizes all components in the correct order
     * 5. Updates form metadata (type, sort order)
     *
     * This is useful for importing forms from external sources or restoring forms from backups.
     * Note that all GUIDs in the new form will be regenerated to ensure uniqueness.
     * @param formDefinitionGuid The GUID of the existing form to replace
     * @param requestBody The complete new form definition structure
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public uploadFormJson(
        formDefinitionGuid?: string,
        requestBody?: FormDefinition,
    ): CancelablePromise<FormDefinition> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/FormDefinition',
            query: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Creates a new form definition with the specified name.
     * This endpoint creates a new, empty form definition with the given name.
     * The form is initialized with a single default section to serve as a container for future form elements.
     * The new form has a SortOrder of 0, which can be modified later if needed.
     *
     * This is typically the first step in the form creation process, after which
     * the form designer would add specific form elements (questions, text blocks, etc.)
     * using the UpdateFormDefinition endpoint.
     * @param formName The name to assign to the new form
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public createFormDefinition(
        formName: string,
    ): CancelablePromise<FormDefinition> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/FormDefinition/{formName}/CreateFormDefinition',
            path: {
                'formName': formName,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Deletes a form definition and all its associated data.
     * This endpoint performs a complete deletion of a form definition, including:
     * - The form definition itself
     * - All form responses submitted for this form
     * - Any file uploads associated with form responses
     *
     * The operation has several security and validation checks:
     * 1. The user must be logged in
     * 2. The user must have appropriate roles in the organization that owns the form
     * 3. The form cannot be a WorkflowDefinitionMainForm (as workflows require at least one main form)
     *
     * When deleting form responses, the method also handles cleanup of any file uploads
     * to prevent orphaned files in the system.
     * @param formDefinitionGuid The unique identifier (GUID) of the form definition to delete
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public deleteFormDefinition(
        formDefinitionGuid: string,
    ): CancelablePromise<FormDefinition> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/api/FormDefinition/{formDefinitionGuid}/DeleteFormDefinition',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
}
